import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import MuiAppBar from '@mui/material/AppBar';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar, Button, Menu, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../features/authSlice';
import { SideBarOptions } from '../const';

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme }) => ({
        flexGrow: 1,
        padding: theme.spacing(3),
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        marginLeft: `-${drawerWidth}px`,
        variants: [
            {
                props: ({ open }) => open,
                style: {
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.easeOut,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    marginLeft: 0,
                },
            },
        ],
    }),
);

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme }) => ({
    background: 'white',
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    variants: [
        {
            props: ({ open }) => open,
            style: {
                width: `calc(100% - ${drawerWidth}px)`,
                marginLeft: `${drawerWidth}px`,
                transition: theme.transitions.create(['margin', 'width'], {
                    easing: theme.transitions.easing.easeOut,
                    duration: theme.transitions.duration.enteringScreen,
                }),
            },
        },
    ],
}));

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
}));

export default function PersistentDrawerLeft({
    children
}) {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const handleProfileMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleProfileMenuClose = () => {
        setAnchorEl(null);
    };

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <AppBar position="fixed" open={open}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            onClick={handleDrawerOpen}
                            edge="start"
                            sx={[
                                {
                                    ml: 0.5,
                                    color: theme.palette.secondary.main
                                },
                                open && { display: 'none' },
                            ]}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Box
                            component="img"
                            src="/Images/headerlogo.png" // Replace with your logo icon path
                            alt="Logo"
                            sx={{
                                width: 'auto',
                                height: '50px',
                                cursor: 'pointer'
                            }}
                            onClick={() => navigate('/admin-dashboard')}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            pl: 1,
                            py: '4px',
                            pr: 1.5,
                            borderRadius: '20px',
                        }}
                        onClick={handleProfileMenuOpen}
                    >
                        <Avatar
                            sx={{
                                width: '45px',
                                height: '45px',
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                background: theme.palette.secondary.main,
                                color: theme.palette.primary.main
                            }}
                        >
                            {`${user?.first_name?.charAt(0)} ${user?.last_name?.charAt(0)}`}
                        </Avatar>
                    </Box>
                </Box>
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleProfileMenuClose}
                    PaperProps={{
                        sx: {
                            mt: 2.3,
                            minWidth: '300px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                            border: '1px solid #E5E7EB'
                        }
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 2.1, pt: 1, pb: 2.5 }}>
                        <Avatar
                            sx={{
                                width: '40px',
                                height: '40px',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                background: theme.palette.secondary.main,
                                color: theme.palette.primary.main
                            }}
                        >
                            {`${user?.first_name?.charAt(0)} ${user?.last_name?.charAt(0)}`}
                        </Avatar>
                        <Box>
                            <Typography sx={{ fontSize: 15, fontWeight: 800 }} variant='h4'>{`${user?.first_name} ${user?.last_name}`}</Typography>
                        </Box>
                    </Box>
                    <Divider />
                    <Button
                        sx={{
                            mt: 1,
                            ml: 1.5,
                            border: '1px solid lightgray',
                            color: theme.palette.text.main,
                            width: '92%',
                            '&:hover': {
                                border: '1px solid lightgray',
                                color: theme.palette.text.main,
                                background: 'white'
                            }
                        }}
                        variant='outlined'
                        onClick={() => {
                            handleProfileMenuClose();
                            dispatch(logout());
                            navigate('/admin');
                        }}
                    >
                        Logout
                    </Button>
                </Menu>
            </AppBar>
            <Drawer
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        background: theme.palette.secondary.main
                    },
                }}
                variant="persistent"
                anchor="left"
                open={open}
            >
                <DrawerHeader>
                    <IconButton onClick={handleDrawerClose}>
                        {theme.direction === 'ltr' ? <ChevronLeftIcon sx={{ color: 'white' }} /> : <ChevronRightIcon sx={{ color: 'white' }} />}
                    </IconButton>
                </DrawerHeader>
                <Divider sx={{ borderColor: 'white' }} />
                <List>
                    {SideBarOptions.map((text, index) => (
                        <ListItem key={index} disablePadding>
                            <ListItemButton onClick={() => {
                                navigate(text.link);
                                handleDrawerClose();
                            }}>
                                <ListItemIcon sx={{ minWidth: 40 }}>
                                    {text.icon}
                                </ListItemIcon>
                                <ListItemText primary={text.label} sx={{ color: 'white' }} />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            </Drawer>
            <Main open={open}>
                <DrawerHeader />
                <Box pt={2}>
                    {children}
                </Box>
            </Main>
        </Box>
    );
}